import { useState, useCallback, useRef, useEffect } from 'react';
import { ETL_COPILOT_WEB_SOCKET_URL } from '../../../../BaseUrl';

/**
 * Sends { sources, destinations, mappings } to generate-sql WebSocket.
 * On type "complete", stores and logs pyspark_sql and duckdb_sql.
 */
export function useGenerateSqlWebSocket() {
  const wsRef = useRef(null);
  const [pysparkSql, setPysparkSql] = useState('');
  const [duckdbSql, setDuckdbSql] = useState('');
  const [status, setStatus] = useState('idle'); // idle | loading | complete | error
  const [lastMessage, setLastMessage] = useState(null);

  useEffect(() => {
    return () => {
      if (wsRef.current) {
        try {
          wsRef.current.close();
        } catch (err) {
          console.error('Error closing WebSocket:', err);
        }
        wsRef.current = null;
      }
    };
  }, []);

  const sendPayload = useCallback((payload) => {
    if (!payload) {
      console.log('Generate SQL WebSocket: skipped (no payload)');
      return;
    }

    if (wsRef.current) {
      try {
        wsRef.current.close();
      } catch (err) {
        console.error('Error closing WebSocket:', err);
      }
      wsRef.current = null;
    }

    setPysparkSql('');
    setDuckdbSql('');
    setStatus('loading');
    setLastMessage(null);

    const ws = new WebSocket(`${ETL_COPILOT_WEB_SOCKET_URL}/generate-sql`);
    wsRef.current = ws;

    ws.onopen = () => {
      const body = {
        sources: payload.sources ?? [],
        destinations: payload.destinations ?? [],
        mappings: Array.isArray(payload.mappings) ? payload.mappings : [],
      };
      ws.send(JSON.stringify(body));
      console.log('Generate SQL WebSocket: sent payload', body);
    };

    ws.onmessage = (event) => {
      try {
        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        console.log('Generate SQL WebSocket response:', data);
        setLastMessage(data);

        if (data && data.type === 'progress') {
          setStatus('loading');
        } else if (data && data.type === 'complete') {
          const pySql = data.pyspark_sql != null ? String(data.pyspark_sql) : '';
          const dbSql = data.duckdb_sql != null ? String(data.duckdb_sql) : '';
          setPysparkSql(pySql);
          setDuckdbSql(dbSql);
          console.log('Generate SQL WebSocket — pyspark_sql:', pySql);
          console.log('Generate SQL WebSocket — duckdb_sql:', dbSql);
          if (Array.isArray(data.validation_errors) && data.validation_errors.length > 0) {
            console.log('Generate SQL WebSocket — validation_errors:', data.validation_errors);
          }
          setStatus('complete');
        }
      } catch (e) {
        console.log('Generate SQL WebSocket response (raw):', event.data);
      }
    };

    ws.onerror = (err) => {
      console.log('Generate SQL WebSocket error:', err);
      setStatus('error');
    };

    ws.onclose = (ev) => {
      wsRef.current = null;
      console.log('Generate SQL WebSocket closed:', ev.code, ev.reason);
    };
  }, []);

  return { sendPayload, pysparkSql, duckdbSql, status, lastMessage };
}
